import { useMemo } from "react";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { BaseInput, Checkbox, CheckboxSize, Show } from "shared/components";
import { InputSize } from "shared/components/forms/BaseInput";
import { Pagination } from "shared/components/Pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { cx } from "shared/utils";
import { Table } from "shared/app/Table";
import { TransactionsEmptyState } from "../TransactionsEmptyState";
import { BankStatementTransaction } from "../types";
import { belongsToOtherIncome, columnHelper, getSharedColumns } from "../utils";
import { useEditViewController } from "../controllers/useEditViewController";
import { Switch } from "shared/components/forms/Switch";

type EditViewController = ReturnType<typeof useEditViewController>;

type Props = {
  edit: EditViewController;
  incomeId?: number;
};

export const EditView = ({ edit, incomeId }: Props) => {
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "fixed_width checkbox custom_",
        size: 48,
        cell: ({ row }) => {
          const tx = row.original as unknown as BankStatementTransaction;
          const disabledByOtherIncome = belongsToOtherIncome(tx, incomeId);
          const isDisabled = disabledByOtherIncome || !tx.assignable;
          const isChecked = disabledByOtherIncome
            ? true
            : Boolean(edit.selection[tx.id]);

          const checkbox = (
            <Checkbox
              size={CheckboxSize.SM}
              checked={isChecked}
              disabled={isDisabled}
              onChange={() => edit.toggleRow(tx)}
              className={cx(isDisabled && "opacity-50")}
            />
          );

          if (!disabledByOtherIncome)
            return <div className="px-4">{checkbox}</div>;

          return (
            <div className="px-4">
              <Tooltip placement="top" delay={{ open: 300 }}>
                <TooltipTrigger asChild>
                  <span>{checkbox}</span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[200px] rounded-lg border border-alpha-40 bg-alpha-800 px-3 py-2 text-sm text-white z-[1000]">
                  This transaction was used to verify another income source
                </TooltipContent>
              </Tooltip>
            </div>
          );
        }
      }),
      ...getSharedColumns(
        (row) =>
          belongsToOtherIncome(
            row as unknown as BankStatementTransaction,
            incomeId
          ),
        (row) =>
          Boolean(
            edit.selection[(row as unknown as BankStatementTransaction).id]
          )
      )
    ],
    [incomeId, edit.selection, edit.toggleRow]
  );

  const table = useReactTable({
    data: edit.items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id)
  });

  return (
    <div className="px-6 py-5 flex flex-col gap-3 bg-background-900 flex-1">
      <div className="flex items-center justify-between">
        <BaseInput
          id="search"
          type="search"
          value={edit.search}
          onChange={(val: string) => edit.setSearch(val)}
          size={InputSize.SM}
          className="max-w-[50%] w-full"
          placeholder="Search deposit"
        />

        {edit.selectionIds.length === 0 ? (
          <Tooltip placement="top" delay={{ open: 300 }}>
            <TooltipTrigger asChild>
              <span>
                <Switch
                  className="font-medium"
                  checked={edit.showSelectedOnly}
                  disabled
                  label="Show selected only"
                  onChange={edit.setShowSelectedOnly}
                />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[220px] rounded-lg border border-alpha-40 bg-alpha-800 px-3 py-2 text-sm text-white z-[1000]">
              There are no selected items
            </TooltipContent>
          </Tooltip>
        ) : (
          <Switch
            className="font-medium"
            checked={edit.showSelectedOnly}
            label="Show selected only"
            onChange={edit.setShowSelectedOnly}
          />
        )}
      </div>

      {edit.isEmpty ? (
        <TransactionsEmptyState />
      ) : (
        <Table
          tableClassName=""
          rowHeight="48px"
          thClassName="!px-4 !py-3 [&_.group]:!gap-1 [&_.group_span]:!ml-0 [&_.group_span]:!opacity-100"
          tdClassName="!py-1 !px-4"
          table={table}
          disableMobileListView
          isLoading={edit.isLoading}
          sortableKeys={["description", "date", "amount"]}
          sorted={edit.sortingState}
          setSorted={edit.setSortingState}
          getRowClassName={(row) =>
            belongsToOtherIncome(
              row.original as unknown as BankStatementTransaction,
              incomeId
            )
              ? "bg-background-900 opacity-60"
              : ""
          }
        />
      )}
      <Show if={edit.totalPages > 1}>
        <Pagination
          className="justify-end"
          total={edit.totalPages}
          current={edit.page}
          onChange={edit.setPage}
        />
      </Show>
    </div>
  );
};
